import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { SimpleTooltip } from '@eluve/components';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  FormField,
  Icon,
  Input,
  Label,
  Separator,
  Stack,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

const userFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid user email'),
});

const updateUserMutation = graphql(`
  mutation updateUser($userId: uuid!, $data: UsersSetInput!) {
    updateUsersByPk(pkColumns: { id: $userId }, _set: $data) {
      id
      firstName
      lastName
      email
    }
  }
`);

type UserFormValues = z.infer<typeof userFormSchema>;

interface UpdateUserActionProps {
  userId: string;
  defaultValues: UserFormValues;
}

export const UpdateUserAction: React.FC<UpdateUserActionProps> = ({
  userId,
  defaultValues,
}) => {
  const toast = useToast();
  const { control, formState, handleSubmit, reset } = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues,
  });

  const [updateUser] = useMutation(updateUserMutation, {
    onCompleted: ({ updateUsersByPk }) => {
      if (!updateUsersByPk) {
        return;
      }

      toast.success('User updated');
      reset(updateUsersByPk);
      setOpen(false);
    },
    onError: () => {
      toast.error('Failed to update user');
    },
  });

  const [open, setOpen] = useState(false);

  const handleUpdateUser = handleSubmit((data) => {
    return updateUser({
      variables: {
        userId,
        data,
      },
    });
  });

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <SimpleTooltip label="Update user">
        <DialogTrigger asChild>
          <Button variant="outline" aria-label="Update user">
            <Icon name="Edit" size="xl" />
          </Button>
        </DialogTrigger>
      </SimpleTooltip>
      <DialogContent size="lg">
        <Stack direction="horizontal" gap={3} align="stretch">
          <Icon name="UserCog" size="xl" />
          <DialogHeader>
            <DialogTitle className="font-normal tracking-normal text-text-secondary">
              Update user
            </DialogTitle>
          </DialogHeader>
        </Stack>

        <Separator className="my-2" />

        <Label>
          <Stack fullWidth={false} gap={2}>
            First Name
            <FormField
              control={control}
              name="firstName"
              render={({ field }) => (
                <Input placeholder="First Name" {...field} />
              )}
            />
          </Stack>
        </Label>

        <Label>
          <Stack fullWidth={false} gap={2}>
            Last Name
            <FormField
              control={control}
              name="lastName"
              render={({ field }) => (
                <Input placeholder="Last Name" {...field} />
              )}
            />
          </Stack>
        </Label>

        <Label>
          <Stack fullWidth={false} gap={2}>
            Email
            <FormField
              control={control}
              name="email"
              render={({ field }) => <Input placeholder="Email" {...field} />}
            />
          </Stack>
        </Label>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateUser}
            type="button"
            disabled={
              formState.isSubmitting || !formState.isValid || !formState.isDirty
            }
          >
            Update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
